import { DualcamServicePromiseClient } from "../generated/dualcam_grpc_web_pb";
import {
    ListAlbumsRequest,
    AddAlbumRequest,
    UpdateAlbumRequest,
    DeleteAlbumRequest,
    GetAlbumMediaRequest,
    GetAlbumStatsRequest,
    CheckUploadNamesRequest,
    CreatePublicShareRequest,
    ListPublicSharesRequest,
    ListAllPublicSharesRequest,
    RevokePublicShareRequest,
    PublicShare as PublicShareProto,
    ShareAlbumRequest,
    ListAlbumSharesRequest,
    RevokeShareRequest,
    Album as AlbumProto,
    AlbumShare as AlbumShareProto,
    MediaEntry as MediaEntryProto,
    SetItemContributorsRequest,
    ListAssignableUsersRequest,
} from "../generated/dualcam_pb";
import DUALCAM_BACKEND_URL from "./dualcamConfig";
import type { Album, AlbumMedia, AlbumShare, AlbumStats, AssignableUser, DuplicateMatch, MediaEntry, PublicShare, PublicShareView } from "./types";
import { generateThumbs } from "./thumbnails";
import { StatusCode } from "grpc-web";
import { notification } from "antd";

class DualcamApiError extends Error {
    // Carries the RPC's grpc status code (e.g. StatusCode.NOT_FOUND) so callers can distinguish
    // "you don't/no longer have access" from a generic failure instead of only having a message string.
    code: StatusCode;

    constructor(message: string, code: StatusCode) {
        super(message);
        this.name = "DualcamApiError";
        this.code = code;
    }
}

/** True when a failure means "this album isn't yours to see" — deleted, unshared, or never yours — as
 *  opposed to a transient error. The backend answers NOT_FOUND for all of those (GetAuthenticatedClientAsync
 *  re-checks ownership per request), so this is the one place that mapping is written down; every page that
 *  distinguishes "no access" from "empty album" calls it instead of re-testing the status code. */
export const isNoAccessError = (err: unknown): boolean =>
    err instanceof DualcamApiError && err.code === StatusCode.NOT_FOUND;

const client = new DualcamServicePromiseClient(DUALCAM_BACKEND_URL, null, null);

// Every RPC embeds the caller's token as a request field (validated server-side via
// STD_Database's JwtService) rather than a metadata/Authorization header — matches how the rest
// of this app already calls Saufdings' Usernator service (see AuthContext.tsx/FriendsPage.tsx).
const rpcMessages: Partial<Record<StatusCode, [de: string, en: string]>> = {
    [StatusCode.NOT_FOUND]: ["Das Album ist nicht verfügbar oder nicht mehr für dich freigegeben.", "This album is unavailable or no longer shared with you."],
    [StatusCode.UNAUTHENTICATED]: ["Bitte melde dich erneut an.", "Please sign in again."],
    [StatusCode.PERMISSION_DENIED]: ["Du hast keine Berechtigung für diese Aktion.", "You don't have permission to do this."],
    [StatusCode.INVALID_ARGUMENT]: ["Bitte prüfe deine Eingaben und versuche es erneut.", "Check your entries and try again."],
};
const fallbackMessage: [string, string] = ["Die Anfrage konnte nicht abgeschlossen werden. Prüfe deine Verbindung und versuche es erneut.", "The request could not be completed. Check your connection and try again."];
const wrap = async <T>(fn: () => Promise<T>): Promise<T> => {
    try {
        return await fn();
    } catch (err) {
        const rpcErr = err as { message?: string; code?: StatusCode } | undefined;
        const code = rpcErr?.code ?? StatusCode.UNKNOWN;
        const isGerman = navigator.language.toLowerCase().startsWith("de");
        const [de, en] = rpcMessages[code] ?? fallbackMessage;
        const message = isGerman ? de : en;
        throw new DualcamApiError(message, code);
    }
};

const toAlbum = (a: AlbumProto): Album => ({
    id: a.getId(),
    host: a.getHost(),
    sharingId: a.getSharingid(),
    label: a.hasLabel() ? a.getLabel() : null,
    enabled: a.getEnabled(),
    shared: a.getShared(),
    canUpload: a.getCanupload(),
    sharedByUsername: a.hasSharedbyusername() ? a.getSharedbyusername() : null,
});

const toAlbumShare = (s: AlbumShareProto): AlbumShare => ({
    shareId: s.getShareid(),
    friendUserId: s.getFrienduserid(),
    friendUsername: s.getFriendusername(),
});

const toMediaEntry = (m: MediaEntryProto): MediaEntry => ({
    id: m.getId(),
    kind: m.getKind() as MediaEntry["kind"],
    mediaType: m.getMediatype() as MediaEntry["mediaType"],
    timestamp: m.getTimestamp(),
    thumbnailUrl: m.getThumbnailurl(),
    frontThumbnailUrl: m.hasFrontthumbnailurl() ? m.getFrontthumbnailurl() : null,
    backUrl: m.hasBackurl() ? m.getBackurl() : null,
    frontUrl: m.hasFronturl() ? m.getFronturl() : null,
    durationMs: m.hasDurationms() ? m.getDurationms() : null,
    latitude: m.hasLatitude() ? m.getLatitude() : null,
    longitude: m.hasLongitude() ? m.getLongitude() : null,
    width: m.hasWidth() ? m.getWidth() : null,
    height: m.hasHeight() ? m.getHeight() : null,
    contributor: m.getContributor(),
    originalContributor: m.getOriginalcontributor(),
});

export const listAlbums = (token: string) => wrap(async () => {
    const req = new ListAlbumsRequest();
    req.setToken(token);
    const res = await client.listAlbums(req);
    return res.getAlbumsList().map(toAlbum);
});

export const addAlbum = (
    token: string,
    url: string,
    password: string | null,
    label: string | null,
    // Optional Photo Request link — enables uploads at link time instead of a second trip through the
    // Share dialog. Validated server-side against the NAS, so a bad link fails the add.
    uploadRequestUrl: string | null = null
) => wrap(async () => {
    const req = new AddAlbumRequest();
    req.setToken(token);
    req.setUrl(url);
    if (password != null) req.setPassword(password);
    if (label != null) req.setLabel(label);
    if (uploadRequestUrl != null) req.setUploadrequesturl(uploadRequestUrl);
    return toAlbum(await client.addAlbum(req));
});

export const updateAlbum = (
    token: string,
    id: number,
    // uploadRequestUrl follows password's convention: omit to leave alone, pass "" to detach the
    // album's Photo Request link and make it read-only again.
    changes: { label?: string | null; enabled?: boolean; password?: string | null; uploadRequestUrl?: string | null }
) => wrap(async () => {
    const req = new UpdateAlbumRequest();
    req.setToken(token);
    req.setId(id);
    if (changes.label != null) req.setLabel(changes.label);
    if (changes.enabled != null) req.setEnabled(changes.enabled);
    if (changes.password != null) req.setPassword(changes.password);
    if (changes.uploadRequestUrl != null) req.setUploadrequesturl(changes.uploadRequestUrl);
    return toAlbum(await client.updateAlbum(req));
});

export const getAlbumStats = (token: string, albumId: number) => wrap(async () => {
    if (!Number.isSafeInteger(albumId) || albumId <= 0) throw new DualcamApiError("", StatusCode.NOT_FOUND);
    const req = new GetAlbumStatsRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    const res = await client.getAlbumStats(req);
    const stats: AlbumStats = {
        totalItems: res.getTotalitems(),
        totalBytes: res.getTotalbytes(),
        contributors: res.getContributorsList().map(c => ({
            name: c.getName(),
            photos: c.getPhotos(),
            videos: c.getVideos(),
            bytes: c.getBytes(),
            durationMs: c.getDurationms(),
            firstEpochSec: c.getFirstepochsec(),
            lastEpochSec: c.getLastepochsec(),
            days: c.getDaysList().map(d => ({
                date: d.getDate(),
                photos: d.getPhotos(),
                videos: d.getVideos(),
                bytes: d.getBytes(),
                durationMs: d.getDurationms(),
            })),
        })),
    };
    return stats;
});

/** Checks filenames (no bytes) against what's already in the album, so the user can be asked before a
 *  second copy is added — Synology renames a colliding upload to " (1)" rather than refusing it.
 *  Advisory: matching is by normalized name, so two different photos sharing a filename look alike. */
export const checkUploadNames = (token: string, albumId: number, names: string[]) => wrap(async () => {
    const req = new CheckUploadNamesRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    req.setNamesList(names);
    const res = await client.checkUploadNames(req);
    const duplicates: DuplicateMatch[] = res.getMatchesList().map(m => ({
        name: m.getName(),
        existingFilenames: m.getExistingfilenamesList(),
    }));
    return duplicates;
});

const toPublicShare = (s: PublicShareProto): PublicShare => ({
    shareId: s.getShareid(),
    albumId: s.getAlbumid(),
    shareToken: s.getSharetoken(),
    itemIds: s.getItemidsList(),
    createdAtEpochSec: s.getCreatedatepochsec(),
    expiresAtEpochSec: s.getExpiresatepochsec(),
});

/** Viewer-visible count, not stored NAS ids: a DualCam pair is one item made of two ids. Unrecognised ids (e.g. deleted since) count as one each. */
export const sharedItemCount = (items: MediaEntry[], share: PublicShare): number => {
    const ids = new Set(share.itemIds.map(String));
    const matched = items.filter(entry => rawIdsFor(entry).every(id => ids.has(id)));
    const accountedFor = matched.reduce((sum, entry) => sum + rawIdsFor(entry).length, 0);
    return matched.length + (share.itemIds.length - accountedFor);
};

/** Mints an unlisted, no-login link to the given items. `expiresInHours` is clamped server-side to
 *  1…168 (7 days) — pass what the user picked and trust the clamp rather than duplicating it here. */
export const createPublicShare = (token: string, albumId: number, itemIds: number[], expiresInHours: number) => wrap(async () => {
    const req = new CreatePublicShareRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    req.setItemidsList(itemIds);
    req.setExpiresinhours(expiresInHours);
    return toPublicShare(await client.createPublicShare(req));
});

/** Every live link this user created, across all their albums — the one place to find and turn off a
 *  link later. Not album-scoped on purpose: a link outlives the album view it was made from. */
export const listAllPublicShares = (token: string) => wrap(async () => {
    const req = new ListAllPublicSharesRequest();
    req.setToken(token);
    const res = await client.listAllPublicShares(req);
    return res.getSharesList().map(toPublicShare);
});

export const listPublicShares = (token: string, albumId: number) => wrap(async () => {
    const req = new ListPublicSharesRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    const res = await client.listPublicShares(req);
    return res.getSharesList().map(toPublicShare);
});

export const revokePublicShare = (token: string, shareId: number) => wrap(async () => {
    const req = new RevokePublicShareRequest();
    req.setToken(token);
    req.setShareid(shareId);
    await client.revokePublicShare(req);
});

/** The visitor-facing URL for a share — what actually gets sent to someone. Built from the browser's
 *  own origin rather than the API host, since the page that renders it is served by the frontend. */
export const publicShareUrl = (shareToken: string): string =>
    `${window.location.origin}/s/${shareToken}`;

/** Loads a public share. Plain fetch, no token and no gRPC: the whole point is that the visitor has no
 *  dowtex account. A dead link (unknown, expired or revoked — the backend deliberately can't tell you
 *  which) comes back 404, which callers should show as "this link no longer works". */
export const fetchPublicShare = async (shareToken: string): Promise<PublicShareView> => {
    const response = await fetch(`${DUALCAM_BACKEND_URL}/public/${encodeURIComponent(shareToken)}`);
    if (response.status === 404) throw new Error("expired");
    if (!response.ok) throw new Error(`Couldn't load this link (HTTP ${response.status})`);
    return await response.json();
};

export type UploadOutcome = {
    uploaded: { name: string; id: number }[];
    failed: { name: string; error: string }[];
};

/**
 * Uploads files into an album via the backend's plain-REST /media/upload (not gRPC — raw binary
 * bodies would have to be base64'd into a grpc-web message, and the browser's own FormData upload is
 * the natural client for this). Only works for an album whose owner has attached a Photo Request link,
 * i.e. `album.canUpload`.
 *
 * XMLHttpRequest rather than fetch: fetch still has no upload-progress event, and a phone video over a
 * home upstream link is exactly the case where a progress bar matters. Partial success is normal — one
 * rejected file doesn't abandon the rest — so both lists come back and callers should report `failed`.
 */
export type UploadProgress = {
    percent: number;
    /** 1-based index of the file whose bytes are currently going out, for "3 of 7" style feedback. */
    currentFile: number;
    fileCount: number;
};

export const uploadToAlbum = async (
    token: string,
    albumId: number,
    files: File[],
    onProgress?: (progress: UploadProgress) => void
): Promise<UploadOutcome> => {
    const form = new FormData();
    // Byte offset at which each file's segment ends, so upload progress can be translated into "which
    // file is going out right now" — a single multipart request gives one byte counter, not per-file
    // events, and splitting into one request per file would cost a fresh NAS session for each.
    const segmentEnds: number[] = [];
    let cumulative = 0;

    for (let index = 0; index < files.length; index++) {
        const file = files[index];
        form.append("file", file, file.name);
        cumulative += file.size;

        // Previews are generated here rather than server-side because only the browser already has the
        // decoded bytes; see thumbnails.ts for why sending them matters. Failure yields none and the
        // upload proceeds — never block an upload on a preview.
        const thumbs = await generateThumbs(file);
        for (const [key, blob] of Object.entries(thumbs)) {
            if (!blob) continue;
            form.append(`thumb_${key}_${index}`, blob, "blob");
            cumulative += blob.size;
        }
        segmentEnds.push(cumulative);
    }

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${DUALCAM_BACKEND_URL}/media/upload?albumId=${albumId}`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        xhr.upload.onprogress = event => {
            if (!event.lengthComputable || !onProgress) return;
            // Multipart framing makes event.total slightly larger than the sum of the parts, so scale the
            // byte counter into the parts' own space before locating it — otherwise the file counter lags.
            const bodyBytes = cumulative > 0 ? (event.loaded / event.total) * cumulative : 0;
            const reached = segmentEnds.findIndex(end => bodyBytes < end);
            onProgress({
                percent: Math.round((event.loaded / event.total) * 100),
                currentFile: (reached === -1 ? files.length : reached + 1),
                fileCount: files.length,
            });
        };

        xhr.onload = () => {
            let parsed: (UploadOutcome & { error?: string }) | null = null;
            try {
                parsed = JSON.parse(xhr.responseText);
            } catch {
                parsed = null;
            }

            if (xhr.status >= 200 && xhr.status < 300 && parsed) {
                resolve({ uploaded: parsed.uploaded ?? [], failed: parsed.failed ?? [] });
                return;
            }
            // 404 = no access to this album (or it's gone), 409 = the owner never attached a Photo
            // Request link, 502 = every file was rejected by the NAS. The body carries the detail in
            // each case, so prefer it over inventing a message per status.
            reject(new Error(parsed?.error ?? parsed?.failed?.[0]?.error ?? `Upload failed (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Upload failed — couldn't reach the server."));
        xhr.onabort = () => reject(new Error("Upload cancelled."));

        xhr.send(form);
    });
};

export const deleteAlbum = (token: string, id: number) => wrap(async () => {
    const req = new DeleteAlbumRequest();
    req.setToken(token);
    req.setId(id);
    await client.deleteAlbum(req);
});

export const shareAlbum = (token: string, albumId: number, friendUserId: number) => wrap(async () => {
    const req = new ShareAlbumRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    req.setFrienduserid(friendUserId);
    await client.shareAlbum(req);
});

export const listAlbumShares = (token: string, albumId: number) => wrap(async () => {
    const req = new ListAlbumSharesRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    const res = await client.listAlbumShares(req);
    return res.getSharesList().map(toAlbumShare);
});

export const revokeShare = (token: string, albumId: number, shareId: number) => wrap(async () => {
    const req = new RevokeShareRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    req.setShareid(shareId);
    await client.revokeShare(req);
});

/** `includeGps` is for the map only: resolving coordinates Synology didn't index costs the backend up to
 *  two NAS fetches per video, which dominates the response on a large album. The grid never reads them. */
export const getAlbumMedia = (token: string, albumId: number, includeGps = false) => wrap(async (): Promise<AlbumMedia> => {
    if (!Number.isSafeInteger(albumId) || albumId <= 0) throw new DualcamApiError("", StatusCode.NOT_FOUND);
    const req = new GetAlbumMediaRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    req.setIncludegps(includeGps);
    const res = await client.getAlbumMedia(req);
    return { items: res.getItemsList().map(toMediaEntry), canUpload: res.getCanupload(), isOwner: res.getIsowner() };
});

/** Credits `entries` to `userId`, or clears the override when `userId` is 0 so they fall back to what
 *  their filenames say. Album-owner only, enforced server-side (PERMISSION_DENIED otherwise). Sends
 *  MediaEntry ids, not raw NAS ids — a pair is one credit, not one per half. */
export const setItemContributors = (token: string, albumId: number, itemIds: string[], userId: number) => wrap(async () => {
    const req = new SetItemContributorsRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    req.setItemidsList(itemIds);
    req.setUserid(userId);
    await client.setItemContributors(req);
});

/** Who this album's items may be credited to: its participants, then the viewer's other friends. */
export const listAssignableUsers = (token: string, albumId: number) => wrap(async (): Promise<AssignableUser[]> => {
    const req = new ListAssignableUsersRequest();
    req.setToken(token);
    req.setAlbumid(albumId);
    const res = await client.listAssignableUsers(req);
    return res.getUsersList().map(user => ({
        userId: user.getUserid(),
        username: user.getUsername(),
        isParticipant: user.getIsparticipant(),
    }));
});

/** Unpacks the underlying NAS item id(s) carried inside a MediaEntry's id. The backend's MapEntry
 *  (MediaService/DualcamGrpcService) builds that id as "nas_<backId>_<frontId>" for a pair or
 *  "nas_<id>" for a solo item — the composite string is the only place those raw ids reach the
 *  browser, so downloading a pair (back + front) means splitting it back apart rather than sending
 *  the one combined string as a single id. */
export const rawIdsFor = (entry: MediaEntry): string[] => entry.id.replace(/^nas_/, "").split("_").filter(Boolean);

/**
 * Requests a short-lived download URL with the caller's Bearer token, then lets the browser stream
 * the attachment natively so it can report progress and use the server's Content-Disposition name.
 */
export const triggerDownload = async (token: string, albumId: number, entries: MediaEntry[]): Promise<void> => {
    try {
        const query = new URLSearchParams({ albumId: String(albumId), ids: entries.flatMap(rawIdsFor).join(",") });
        const response = await fetch(`${DUALCAM_BACKEND_URL}/media/download-ticket?${query}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);

        const { url } = await response.json() as { url: string };
        const link = document.createElement("a");
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch {
        const isGerman = navigator.language.toLowerCase().startsWith("de");
        notification.error({ message: isGerman ? "Download fehlgeschlagen" : "Download failed" });
    }
};

export { DualcamApiError };
